import { useQuery } from '@tanstack/react-query';
import { awaitJSON, ToolsLambdaFetch } from '@src/utils/fetch-utils';
import {
  ActiveOTTData,
  CompanyOption,
  DateRange,
  ExpectedBookings,
  NetworkOptions
} from './active-ott-constants';

export const useActiveOttCompanyOptions = (): ReturnType<typeof useQuery<CompanyOption[]>> =>
  useQuery({
    queryKey: ['activeOttCompanyOptions'],
    queryFn: async (): Promise<CompanyOption[]> =>
      awaitJSON<CompanyOption[]>(await ToolsLambdaFetch('/activeott_company_options'))
  });

export const useActiveOttNetworkOptions = (
  dates: DateRange
): ReturnType<typeof useQuery<NetworkOptions>> =>
  useQuery({
    queryKey: ['activeOttNetworkOptions', dates],
    queryFn: async (): Promise<NetworkOptions> =>
      awaitJSON<NetworkOptions>(
        await ToolsLambdaFetch('/activeott_network_options', { params: dates })
      )
  });

export const useActiveOttExpectedBookings = (
  dates: DateRange,
  company: string | false
): ReturnType<typeof useQuery<ExpectedBookings>> =>
  useQuery({
    queryKey: ['activeOttExpectedBookings', dates, company],
    queryFn: async (): Promise<ExpectedBookings> =>
      awaitJSON<ExpectedBookings>(
        await ToolsLambdaFetch('/get_expected_bookings_range', {
          params: { ...dates, company, types: 'streaming,audio' }
        })
      ),
    enabled: Boolean(company)
  });

export const useActiveOttData = ({
  dates,
  selectedGroup,
  byNetwork
}: {
  dates: DateRange;
  selectedGroup: CompanyOption | string | undefined;
  byNetwork: boolean;
}): ReturnType<typeof useQuery<ActiveOTTData>> =>
  useQuery({
    queryKey: ['activeOttData', dates, byNetwork, selectedGroup],
    queryFn: async (): Promise<ActiveOTTData> => {
      const params: Record<string, string> = { ...dates };
      if (byNetwork && typeof selectedGroup === 'string') {
        params.network = selectedGroup;
      } else if (!byNetwork && typeof selectedGroup === 'object') {
        params.company = selectedGroup.cid;
      }
      return awaitJSON<ActiveOTTData>(await ToolsLambdaFetch('/activeott', { params }));
    },
    enabled: Boolean(selectedGroup)
  });
